import React, {useCallback, useEffect, useRef, useState} from "react";
import PropTypes from "prop-types";
import * as Colors from "js/common/cube19-colors";
import {CustomThemeContext} from "js/common/themes/CustomThemeProvider";

const Tooltip = ({
  tooltipHtml,
  text,
  position,
  positionOffset,
  dynamicPosition = true,
  showOnHover = true,
  styleType,
  customStyle,
  maxWidth,
  wrap,
  disable = false,
  textSize,
  width,
  arrowSize = 8,
  pointerStyle = {},
  theme,
  children
}) => {
  const [isVisible, setIsVisible] = useState(!showOnHover);
  const [adjustedPosition, setAdjustedPosition] = useState(position);
  const [offsetX, setOffsetX] = useState(0);
  const [offsetY, setOffsetY] = useState(0);

  const tooltipRef = useRef(null);
  const contentRef = useRef(null);
  const scrollDebounceRef = useRef(null);

  const adjustTooltipPosition = useCallback(() => {
    const contentRect = contentRef.current?.getBoundingClientRect();
    const tooltipRect = tooltipRef.current?.getBoundingClientRect();

    if (!dynamicPosition || !children || !contentRect || !tooltipRect || !tooltipRect.width || !tooltipRect.height) {
      return;
    }

    const viewportMargin = 10;
    const viewport = window.visualViewport ? {
      width: window.visualViewport.width - (2 * viewportMargin),
      height: window.visualViewport.height - (2 * viewportMargin),
      offsetLeft: window.visualViewport.offsetLeft + viewportMargin,
      offsetTop: window.visualViewport.offsetTop + viewportMargin
    } : {
      width: window.innerWidth - (2 * viewportMargin),
      height: window.innerHeight - (2 * viewportMargin),
      offsetLeft: viewportMargin,
      offsetTop: viewportMargin
    };


    const spaceAroundContent = {
      left: contentRect.left - viewport.offsetLeft - arrowSize,
      right: viewport.offsetLeft + viewport.width - contentRect.right - arrowSize,
      top: contentRect.top - viewport.offsetTop - arrowSize,
      bottom: viewport.offsetTop + viewport.height - contentRect.bottom - arrowSize
    };

    const spaceAroundTooltip = {
      left: tooltipRect.left - viewport.offsetLeft,
      right: viewport.offsetLeft + viewport.width - tooltipRect.right,
      top: tooltipRect.top - viewport.offsetTop,
      bottom: viewport.offsetTop + viewport.height - tooltipRect.bottom
    };

    const positionInfo = {
      left: {
        inverse: "right",
        isVertical: false,
        useInverse: () => (spaceAroundContent.left < tooltipRect.width)
            && (spaceAroundContent.right > spaceAroundContent.left)
      },
      right: {
        inverse: "left",
        isVertical: false,
        useInverse: () => (spaceAroundContent.right < tooltipRect.width)
            && (spaceAroundContent.left > spaceAroundContent.right)
      },
      top: {
        inverse: "bottom",
        isVertical: true,
        useInverse: () => (spaceAroundContent.top < tooltipRect.height)
            && (spaceAroundContent.bottom > spaceAroundContent.top)
      },
      bottom: {
        inverse: "top",
        isVertical: true,
        useInverse: () => (spaceAroundContent.bottom < tooltipRect.height)
            && (spaceAroundContent.top > spaceAroundContent.bottom)
      },
      topRightOffset: {
        inverse: "bottomRightOffset",
        isVertical: true,
        useInverse: () => (spaceAroundContent.top < tooltipRect.height)
            && (spaceAroundContent.bottom > spaceAroundContent.top)
      },
      bottomRightOffset: {
        inverse: "topRightOffset",
        isVertical: true,
        useInverse: () => (spaceAroundContent.bottom < tooltipRect.height)
            && (spaceAroundContent.top > spaceAroundContent.bottom)
      }
    };

    const posInfo = positionInfo[position];
    let newPosition = posInfo.useInverse() ? posInfo.inverse : position;
    let newOffsetX = offsetX;
    let newOffsetY = offsetY;

    const clamp = (num, min, max) => Math.min(Math.max(num, min), max);

    if (posInfo.isVertical) {
      // adjust offsetX (left/right)
      if (tooltipRect.width > viewport.width) {
        newOffsetX += spaceAroundContent.left > spaceAroundContent.right
            ? spaceAroundTooltip.right
            : -spaceAroundTooltip.left;
      } else if (spaceAroundTooltip.left < 0 || offsetX > 0) {
        newOffsetX -= Math.min(spaceAroundTooltip.left, offsetX);
      } else if (spaceAroundTooltip.right < 0 || offsetX < 0) {
        newOffsetX += Math.min(spaceAroundTooltip.right, spaceAroundTooltip.left);
      }
      const maxOffset = Math.max(0, (tooltipRect.width / 2) - (arrowSize * 1.25));
      newOffsetX = clamp(newOffsetX, -maxOffset, maxOffset);
      newOffsetY = 0;
    } else {
      // adjust offsetY (top/bottom)
      if (tooltipRect.height > viewport.height) {
        newOffsetY += spaceAroundContent.top > spaceAroundContent.bottom
            ? spaceAroundTooltip.bottom
            : -spaceAroundTooltip.top;
      } else if (spaceAroundTooltip.top < 0 || offsetY > 0) {
        newOffsetY -= Math.min(spaceAroundTooltip.top, offsetY);
      } else if (spaceAroundTooltip.bottom < 0 || offsetY < 0) {
        newOffsetY += Math.min(spaceAroundTooltip.bottom, spaceAroundTooltip.top);
      }
      const maxOffset = Math.max(0, (tooltipRect.height / 2) - (arrowSize * 1.25));
      newOffsetY = clamp(newOffsetY, -maxOffset, maxOffset);
      newOffsetX = 0;
    }

    setAdjustedPosition(newPosition);
    setOffsetX(newOffsetX);
    setOffsetY(newOffsetY);
  }, [dynamicPosition, position, arrowSize, children, offsetX, offsetY]);

  useEffect(() => {
    if (!dynamicPosition) {
      return;
    }
    adjustTooltipPosition();

    const handleScroll = () => {
      if (isVisible) {
        if (scrollDebounceRef.current) {
          return;
        }

        scrollDebounceRef.current = setTimeout(() => {
          adjustTooltipPosition();
          scrollDebounceRef.current = null;
        }, 100);
      }
    };

    window.addEventListener("scroll", handleScroll, true);

    return () => {
      window.removeEventListener("scroll", handleScroll, true);
      if (scrollDebounceRef.current) {
        clearTimeout(scrollDebounceRef.current);
        scrollDebounceRef.current = null;
      }
    };
  }, [adjustTooltipPosition, dynamicPosition, isVisible]);

  const handleMouseIn = useCallback(() => {
    if (disable) {
      return;
    }

    showOnHover && setIsVisible(true);
  }, [disable, showOnHover]);

  const handleMouseOut = useCallback(() => {
    if (disable) {
      return;
    }

    showOnHover && setIsVisible(false);
  }, [disable, showOnHover]);

  const closeTooltip = useCallback(() => {
    setIsVisible(false);
  }, []);

  const getTooltipStyle = useCallback(() => {
    const tooltipBaseline = `calc(100% + ${arrowSize}px)`;
    const transformVertical = `translateX(calc(-50% + ${offsetX}px))`;
    const transformVerticalRightOffset = `translateX(calc(50% + ${offsetX}px))`;
    const transformHorizontal = `translateY(calc(-50% + ${offsetY}px))`;

    const tooltipStyleByPosition = {
      top: {
        transform: transformVertical,
        bottom: tooltipBaseline,
        left: "50%"
      },
      topRightOffset: {
        transform: transformVerticalRightOffset,
        bottom: tooltipBaseline,
        right: positionOffset ? positionOffset : 0
      },
      bottom: {
        transform: transformVertical,
        top: tooltipBaseline,
        left: "50%"
      },
      bottomRightOffset: {
        transform: transformVerticalRightOffset,
        top: tooltipBaseline,
        right: positionOffset ? positionOffset : 0
      },
      right: {
        transform: transformHorizontal,
        left: tooltipBaseline,
        top: "50%"
      },
      left: {
        transform: transformHorizontal,
        right: tooltipBaseline,
        top: "50%"
      }
    };

    return {
      ...styles.tooltip,
      display: isVisible ? "inline" : "none",
      maxWidth: maxWidth,
      whiteSpace: wrap ? null : "nowrap",
      fontSize: textSize ? `${textSize}px` : "0.8rem",
      width: width,
      ...customStyle,
      ...tooltipStyleByPosition[adjustedPosition],
      ...tooltipByStyleType[styleType || theme?.themeId]
    };
  }, [
    arrowSize,
    offsetX,
    offsetY,
    positionOffset,
    isVisible,
    maxWidth,
    wrap,
    textSize,
    customStyle,
    adjustedPosition,
    styleType,
    theme?.themeId,
    width
  ]);

  const getTooltipArrowStyle = useCallback(() => {
    const arrowOffset = arrowSize / 2;
    const verticalTooltipLeft = `calc(50% - ${offsetX + arrowOffset}px)`;
    const horizontalTooltipTop = `calc(50% - ${offsetY + arrowOffset}px)`;

    const tooltipArrowStyleByPosition = {
      top: {
        left: verticalTooltipLeft,
        bottom: -arrowOffset
      },
      topRightOffset: {
        left: verticalTooltipLeft,
        bottom: -arrowOffset
      },
      bottom: {
        left: verticalTooltipLeft,
        top: -arrowOffset
      },
      bottomRightOffset: {
        left: verticalTooltipLeft,
        top: -arrowOffset
      },
      right: {
        top: horizontalTooltipTop,
        left: -arrowOffset
      },
      left: {
        top: horizontalTooltipTop,
        right: -arrowOffset
      }
    };

    return {
      ...styles.arrow,
      width: `${arrowSize}px`,
      height: `${arrowSize}px`,
      ...tooltipByStyleType[styleType || theme?.themeId],
      ...tooltipArrowStyleByPosition[adjustedPosition]
    };
  }, [arrowSize, offsetX, offsetY, styleType, adjustedPosition, theme?.themeId]);

  if (disable) {
    return <div style={{position: "relative", ...customStyle}}>{children}</div>;
  }

  return (
      <div
          style={{...styles.container, ...customStyle}}
          onMouseEnter={handleMouseIn}
          onMouseLeave={handleMouseOut}
      >
        <div
            ref={tooltipRef}
            style={getTooltipStyle()}
        >
          {tooltipHtml && tooltipHtml()}
          {text}
          {!showOnHover && (
              <span onClick={closeTooltip} style={styles.closeButton}>
            <i className="fa fa-times-circle" />
          </span>
          )}
          <div style={{...getTooltipArrowStyle(), ...pointerStyle}} />
          <div style={{...styles.arrowBackground, inset: -arrowSize}} />
        </div>
        <div ref={contentRef}>
          {children}
        </div>
      </div>
  );
};

Tooltip.propTypes = {
  tooltipHtml: PropTypes.any,
  text: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  position: PropTypes.oneOf(["top", "bottom", "left", "right", "topRightOffset", "bottomRightOffset"]).isRequired,
  positionOffset: PropTypes.number,
  dynamicPosition: PropTypes.bool,
  showOnHover: PropTypes.bool,
  styleType: PropTypes.oneOf(["yellow", "dark", "light"]),
  customStyle: PropTypes.object,
  maxWidth: PropTypes.number,
  wrap: PropTypes.bool,
  disable: PropTypes.bool,
  textSize: PropTypes.number,
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  arrowSize: PropTypes.number,
  pointerStyle: PropTypes.object,
  theme: PropTypes.object,
  children: PropTypes.node
};

const styles = {
  container: {
    position: "relative"
  },
  tooltip: {
    position: "absolute",
    overflowWrap: "break-word",
    textAlign: "center",
    padding: "10px",
    borderRadius: "5px",
    fontWeight: 400,
    boxShadow: "1px 1px 10px rgba(0,0,0,0.5)",
    zIndex: 99999
  },
  arrow: {
    background: "#0e0e0e",
    position: "absolute",
    transform: "rotate(45deg)",
    zIndex: -1,
    boxShadow: "none !important"
  },
  closeButton: {
    position: "relative",
    zIndex: 9999999,
    marginLeft: "10px",
    cursor: "pointer"
  },
  arrowBackground: {
    background: "transparent",
    position: "absolute",
    zIndex: -1
  }
};

const tooltipByStyleType = {
  dark: {
    background: Colors.trout,
    color: "white"
  },

  yellow: {
    background: "#f9ec33",
    color: "black"
  },

  light: {
    background: "#fff",
    color: "black"
  }
};

const Wrapper = (props) => {
  const {theme} = React.useContext(CustomThemeContext);
  return <Tooltip theme={theme} {...props} />;
};

export default Wrapper;